import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Activity, FileText, GraduationCap, Info, MessageSquare } from 'lucide-react';
import { cn } from '@repo/ui/src/lib/utils';
import type { ClientDetailBundle } from '@repo/shared-types';
import { useClientDetail } from '../../hooks/useClients';
import { useStudents } from '../../hooks/useStudents';
import { formatVnd } from '../../lib/formatVnd';
import { PageHeader } from '../../components/PageHeader';
import { DetailSkeleton } from '../../components/DetailSkeleton';
import { ClientHeader } from './components/ClientHeader';
import { StudentsTab } from './components/StudentsTab';
import { InvoicesTab } from './components/InvoicesTab';
import { NotesTab } from './components/NotesTab';
import { ActivityTab } from './components/ActivityTab';
import { InfoTab } from './components/InfoTab';

const TAB_IDS = ['students', 'invoices', 'notes', 'activity', 'info'] as const;
const TAB_ICONS = {
  students: GraduationCap,
  invoices: FileText,
  notes: MessageSquare,
  activity: Activity,
  info: Info,
} as const;

type TabId = (typeof TAB_IDS)[number];

function formatVNDCompact(amount: number): string {
  if (amount >= 1_000_000) {
    return `${(amount / 1_000_000).toFixed(amount % 1_000_000 === 0 ? 0 : 1)}M`;
  }
  if (amount >= 1_000) {
    return `${(amount / 1_000).toFixed(0)}K`;
  }
  return new Intl.NumberFormat('vi-VN').format(amount);
}

export function ClientDetailPage() {
  const { t } = useTranslation('clients');
  const { id } = useParams();
  const { data: bundle, isLoading, isError } = useClientDetail(id ?? null);

  if (isLoading) return <DetailSkeleton stats />;

  if (isError || !bundle) {
    return (
      <div className="mx-auto w-full max-w-4xl p-8">
        <PageHeader back={{ label: t('detail.backToList'), to: '/clients' }} title={t('detail.notFound')} />
      </div>
    );
  }

  return <ClientDetailView bundle={bundle} />;
}

/**
 * Split from the route component for one concrete reason: the tab strip needs a
 * student COUNT before the Học viên tab is ever opened, and `useStudents` has
 * no `enabled` option — calling it above the not-found guard with an undefined
 * clientId would fire an org-wide student fetch on a page that shows nothing.
 * Here `clientId` is always a real id.
 */
function ClientDetailView({ bundle }: { bundle: ClientDetailBundle }) {
  const { t } = useTranslation('clients');
  // Kept in state so it survives a rotation (13 §1) — nothing resets it.
  const [activeTab, setActiveTab] = useState<TabId>('students');
  const { client, invoices, notes, activities, stats } = bundle;
  // Same query key/options StudentsTab passes, so TanStack serves both from one
  // cache entry and no second request is made.
  const { data: studentPage } = useStudents({ clientId: client.id, pageSize: 100, status: 'all' });

  // Counts only where the number is a countable thing (13 §1). Hoạt động is an
  // unbounded log and Thông tin is a form — neither gets one.
  const counts: Partial<Record<TabId, number>> = {
    students: studentPage?.total,
    invoices: invoices.length,
    notes: notes.length,
  };

  return (
    <div className="mx-auto w-full max-w-4xl p-8">
      <ClientHeader client={client} onEdit={() => setActiveTab('info')} />

      {/* Stat row — 4 on desktop, the 2 that generate action on a phone. Not
          2×2: the invoice count is already on the tab and Doanh thu is a
          look-at number, not a to-do (13 §2). */}
      <div className="mb-[19.2px] grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-[9.6px]">
        <StatCard
          className="hidden md:block"
          label={t('detail.stats.invoiceCount')}
          value={String(stats.totalInvoiceCount)}
        />
        <StatCard
          className="hidden md:block"
          label={t('detail.stats.revenue')}
          value={formatVNDCompact(stats.totalRevenue)}
        />
        <StatCard
          label={t('detail.stats.unpaid')}
          tone={stats.totalUnpaid > 0 ? 'attention' : 'neutral'}
          value={
            <>
              {/* Two tiles have the room for the real number; four do not. */}
              <span className="md:hidden">{formatVnd(stats.totalUnpaid)}</span>
              <span className="hidden md:inline">{formatVNDCompact(stats.totalUnpaid)}</span>
            </>
          }
        />
        <StatCard
          label={t('detail.stats.overdue')}
          value={String(stats.overdueCount)}
          tone={stats.overdueCount > 0 ? 'danger' : 'neutral'}
        />
      </div>

      {/* Five content groups → tabs (13 §1). The strip scrolls horizontally on a
          phone and drops its icons there; segmented would need all five to fit. */}
      <div
        role="tablist"
        aria-label={t('detail.tabsLabel')}
        className="mb-4 flex gap-[4.8px] overflow-x-auto whitespace-nowrap border-b border-surface-200 [scrollbar-width:none] md:overflow-visible"
      >
        {TAB_IDS.map((tabId) => {
          const isActive = activeTab === tabId;
          const Icon = TAB_ICONS[tabId];
          const count = counts[tabId];

          return (
            <button
              key={tabId}
              type="button"
              role="tab"
              id={`client-tab-${tabId}`}
              aria-selected={isActive}
              aria-controls="client-tabpanel"
              onClick={() => setActiveTab(tabId)}
              className={cn(
                '-mb-px inline-flex shrink-0 items-center gap-[4.8px] border-b-2 px-[11.2px] py-[9.6px] text-[13px] transition-colors md:px-[12.8px] md:text-[13.5px]',
                isActive
                  ? 'border-brand-500 font-semibold text-brand-600'
                  : 'border-transparent font-medium text-surface-500 hover:border-surface-300 hover:text-surface-700',
              )}
            >
              <Icon className="hidden h-[14.4px] w-[14.4px] md:inline-block" strokeWidth={1.8} />
              {t(`detail.tabs.${tabId}`)}
              {count !== undefined && (
                <span className="text-[11.5px] font-normal text-surface-400">({count})</span>
              )}
            </button>
          );
        })}
      </div>

      <div role="tabpanel" id="client-tabpanel" aria-labelledby={`client-tab-${activeTab}`}>
        {activeTab === 'students' && <StudentsTab clientId={client.id} clientName={client.name} />}
        {activeTab === 'invoices' && <InvoicesTab invoices={invoices} clientId={client.id} />}
        {activeTab === 'notes' && <NotesTab clientId={client.id} notes={notes} />}
        {activeTab === 'activity' && <ActivityTab clientId={client.id} initialActivities={activities} />}
        {activeTab === 'info' && <InfoTab client={client} />}
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  tone = 'neutral',
  className,
}: {
  label: string;
  value: React.ReactNode;
  /** Same tones the attention system spends elsewhere: ochre for money still
   *  owed, red for money already late, ink for everything else. */
  tone?: 'neutral' | 'attention' | 'danger';
  className?: string;
}) {
  const valueClass =
    tone === 'attention' ? 'text-attention-foreground' : tone === 'danger' ? 'text-destructive' : 'text-surface-900';
  return (
    <div className={cn('rounded-md border border-surface-200 bg-surface-0 px-[12.8px] py-[11.2px] md:px-4 md:py-[12.8px]', className)}>
      <div className={cn('font-display text-[18px] font-semibold tracking-[-0.02em] tabular-nums md:text-[20px]', valueClass)}>
        {value}
      </div>
      <div className="mt-[1.6px] text-xs text-surface-500">{label}</div>
    </div>
  );
}
